<template>
  <div v-if="showAdminWarning" class="p-16 bg-bg-warning text-text-warning type-sm">
    This component is not available on corporate domains
  </div>
  <div
    v-else-if="!isNonEcomSite && !pending && productItem"
    class="ProductPage"
    :class="{'desk:layout-container' : pageType === 'b2b'}"
  >
    <!-- links between b2b / b2c -->
    <nuxt-link
      v-if="pageType === 'b2c' && globalContent.getIsB2b"
      :to="oppositeLink"
      class="block bg-bg-warningStrong text-text-inverse w-full mt-12 mb-24 "
    >
      <div class="layout-container py-12  desk:flex justify-center gap-8">
        <div class="type-headline-base">{{ $t('b2b.wrongPage') }}</div>
        <div v-html="$t('b2b.switchPage')" />
      </div>
    </nuxt-link>
    <nuxt-link
      v-if="pageType === 'b2b' && globalContent.getIsB2c"
      :to="oppositeLink"
      class="block bg-bg-warningStrong text-text-inverse w-full mt-12 mb-24 "
    >
      <div class="layout-container py-12  desk:flex justify-center gap-8">
        <div class="type-headline-base">{{ $t('b2c.wrongPage') }}</div>
        <div v-html="$t('b2c.switchPage')" />
      </div>
    </nuxt-link>

    <!-- top blocks -->
    <div v-if="productContent && productContent.topBlocks">
      <SpaceHandler
        v-for="childBlok in productContent.topBlocks"
        :key="childBlok._uid"
        :blok="childBlok"
      >
        <StoryblokComponent
          :blok="childBlok"
        />
      </SpaceHandler>
    </div>

    <!-- * * * * PRODUCT -->

    <ProductBreadcrumbs
      v-if="!uiStore.isMobile"
      :product-item="productItem"
      :class="{'desk:layout-container' : pageType === 'b2c'}"
    />

    <ProductB2CLayout
      v-if="pageType === 'b2c'"
      :product-item="productItem"
      :active-variant="activeVariant"
      :current-image-list="currentImageList"
      @update-active-variant="(variant) => changeVariant(variant)"
    />

    <ProductExtendedLayout
      v-else-if="productItem.extendedLayout"
      :product-item="productItem"
      :active-variant="activeVariant"
      :current-image-list="currentImageList"
    />

    <ProductDefaultLayout
      v-else
      :product-item="productItem"
      :active-variant="activeVariant"
      :current-image-list="currentImageList"
      :page-type="pageType"
      @update-active-variant="(variant) => changeVariant(variant)"
    />
    <!-- * * * * /PRODUCT -->

    <!-- * * * * direct accessories products -->
    <div class="mb-24 desk:mb-96">
      <ProductAccessories
        v-if="pageType === 'b2b'"
        :product-item="productItem"
        :active-variant="activeVariant"
        design="b2b"
      />
    </div>

    <!-- * * * * related products -->
    <RelatedProductsList
      v-if="pageType === 'b2b'"
      :product-item="productItem"
    />

    <div class="py-24 flex-1">
      <!-- * * * * * * bottom content -->
      <div v-if="productContent && productContent.bottomBlocks">
        <SpaceHandler
          v-for="childBlok in productContent.bottomBlocks"
          :key="childBlok._uid"
          :blok="childBlok"
        >
          <StoryblokComponent
            :blok="childBlok"
          />
        </SpaceHandler>
      </div>
    </div>
  </div>
  <div v-else-if="!isNonEcomSite" class="pt-56 text-center">
    <LoadingIcon />
  </div>
</template>

<script lang="ts" setup>
import Product from '#models/product';
import { useUiStore } from '~/store/ui';
import { useProductStore } from '~/store/product';
import { useGlobalContentStore } from '~/store/globalContent';
import { Head } from '@unhead/schema';
import { StoryBlokProductPageContent } from '#constants/types/storyblok';
import SpaceHandler from '~/components/body/SpaceHandler.vue';
import ProductBreadcrumbs from '~/components/product-page/ProductBreadcrumbs.vue';
import RelatedProductsList from '~/components/product-page/RelatedProductsList.vue';
import ProductExtendedLayout from '~/components/product-page/ProductExtendedLayout.vue';
import ProductDefaultLayout from '~/components/product-page/ProductDefaultLayout.vue';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import { NorceMediaSetCodes } from '#constants/norceCodes';
import domainMapper from '~/util/domain-mapper';
import { createDataLayerItem, sendDataLayer } from '~/composables/useTracking';
import type { ProductVariantModel } from '#models/productVariant';
import ProductAccessories from '~/components/product-page/ProductAccessories.vue';
import ProductB2CLayout from '~/components/product-page/ProductB2CLayout.vue';

import useLinkReplacer from '~/composables/useLinkReplacer';
import useAlgoliaInsights from '~/composables/useAlgoliaInsights';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';

const { isNonEcomSite, showAdminWarning } = useNonEcomGuard();
const uiStore = useUiStore();
const productStore = useProductStore();
const globalContent = useGlobalContentStore();
const route = useRoute();
const splitRoute = route.path.split('/');
const productUrl = ref(splitRoute[splitRoute.length - 1]);
const currentEnvironment = globalContent.getEnvironment;
const nuxtApp = useNuxtApp();
const { $t } = useNuxtApp();

const storyblokApi = useStoryblokApi();
const runtimeConfig = useRuntimeConfig();
const { getProductByUrl } = useAlgoliaFetch();
const pending = ref(true);
const { replaceLinksInPath } = useLinkReplacer();

// * * * * * * * * * b2b vs b2c
const realPath = replaceLinksInPath(route.path)?.replaceAll('//', '/');
const pageType = realPath?.includes(globalContent.domainMap?.b2b?.productBasePath || '') ? 'b2b':'b2c' as 'b2b'|'b2c';
const algoliaIndex = pageType === 'b2b' ? globalContent.domainMap?.b2b?.algoliaIndex : globalContent.domainMap?.b2c?.algoliaIndex;
const oppositeLink = pageType === 'b2b'
  ? `/${globalContent.domainMap?.storyblokBasePath}/${globalContent.domainMap?.b2c?.productBasePath}/${productUrl.value}`
  : `/${globalContent.domainMap?.storyblokBasePath}/${globalContent.domainMap?.b2b?.productBasePath}/${productUrl.value}`;

// * * * * * * * * * Load and initialize
const { data } = await useAsyncData(`product-page-${productUrl.value}`, async() => {
  const sbReq = storyblokApi.get('cdn/stories', {
    starts_with: `${globalContent.getStoryblokBasePath}/${globalContent.getProductBasePath}`,
    filter_query: {
      'product.product.url': {
        in: productUrl.value,
      },
    },
    version: runtimeConfig.public.storyblokStoriesVersion as 'draft' | 'published' | undefined,
  });

  const res = await getProductByUrl(productUrl.value, algoliaIndex);

  return await Promise.all([sbReq, res]);
});

const responses = data.value;
const sbData = responses && responses[0].data?.stories[0];
const productData = ref(responses && responses[1]);

const productContent = ref<StoryBlokProductPageContent | undefined>(sbData?.content);

if (!productData.value || productData.value.hits.length === 0) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' });
}

const productItem = Product.create(productData.value.hits);
const currentImageList = ref<NorceMediaSetCodes>(NorceMediaSetCodes.DefaultImages);

const activeVariant = ref(productItem.variantByUrl(productUrl.value));

const sendProductImpression = async() => {
  if (currentEnvironment !== 'local' && activeVariant.value?.partNo) {
    await productStore.sendProductImpression(activeVariant.value?.partNo);
  }
};

const changeVariant = (variant: ProductVariantModel) => {
  window.history.pushState({ prevVariantUrl: variant.url },'', window.location.href.replace(productUrl.value, variant.url));
  activeVariant.value = variant;
  productUrl.value = variant.url;
  sendProductImpression();
};
onMounted(()=> {
  sendProductImpression();

  window.onpopstate = (e) => {
    if (e.state?.prevVariantUrl) {
      activeVariant.value = productItem.variantByUrl(e.state.prevVariantUrl);
      sendProductImpression();
    }
  };
});

onBeforeUnmount(()=> {
  window.onpopstate  = () => {};
});

pending.value = false;
// * * * * * * * * * Finished initialize

// * * * * Price per square meter calculation
const { convertPackagePriceToAreaPrice, shouldUseArea, conversionType } = usePackageAreaConverter(
  toRef(() => activeVariant.value)
);

const pricePerSquareMeter = computed(() => {
  if (!activeVariant.value || !shouldUseArea.value) {
    return null;
  }

  const basePrice = globalContent.getDisplayIncVat
    ? activeVariant.value.priceInfo.price
    : activeVariant.value.priceInfo.priceBeforeVat;

  return convertPackagePriceToAreaPrice(basePrice);
});

// * * * * recently visited
const recentlyVisited = useCookie<{ url: string, image: string, partNo: string }[]>(
  'recentlyVisited',
  {
    default: () => [],
    watch: 'shallow',
  }
);

if (productItem?.partNo) {
  recentlyVisited.value = recentlyVisited.value?.filter(visitedProduct => visitedProduct.partNo !== productItem.partNo);
  recentlyVisited.value?.unshift({ url: productItem.url, image: productItem.mainImage, partNo: productItem.partNo });
}

// - - - - - - -  SEO Stuff
const head = {} as Head;
const seoMeta = {} as { [key: string]: string | null };
const pageTitle = `${productItem.name} - Kährs`;
head.titleTemplate = ''; // removes auto ' | siteName'

head.title = pageTitle;
seoMeta.title = pageTitle;
seoMeta.ogTitle = pageTitle;
seoMeta.ogImage = productItem.mainImage;
seoMeta.twitterCard = 'summary_large_image';

// Build category hierarchy from categories
const buildCategoryHierarchy = () => {
  const topLevel = productItem.categories.find(c => c.parentCode === null);
  if (!topLevel) {
    return [];
  }

  const hierarchy = [topLevel.name];
  let currentParentCode = topLevel.code;

  // Follow the chain from parent to child
  while (true) {
    const child = productItem.categories.find(c => c.parentCode === currentParentCode);
    if (child) {
      hierarchy.push(child.name);
      currentParentCode = child.code;
    } else {
      break;
    }
  }

  return hierarchy;
};

const categories = buildCategoryHierarchy();

let description = '';
if (productItem.metaDescription && productItem.metaDescription !== '') {
  description = productItem.metaDescription;
} else if (productItem.shortDescription && productItem.shortDescription !== '') {
  description = productItem.shortDescription;
} else if (activeVariant.value.variantDescription && activeVariant.value.variantDescription !== '') {
  description = activeVariant.value.variantDescription;
}

if (description) {
  seoMeta.description = description;
  seoMeta.ogDescription = description;
}

const fullUrl = globalContent.domainMap?.url + '/'
  + (pageType === 'b2b' ? globalContent.domainMap?.b2b?.productBasePath : globalContent.domainMap?.b2c?.productBasePath) + '/'
  + productItem.url;

const jsonLd = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: productItem.name,
  image: productItem.imageList,
  description,
  sku: productItem.partNo,
  mpn: productItem.partNo,
  ...(categories.length > 0 && { category: categories.join(' > ') }),
  brand: {
    '@type': 'Brand',
    name: productItem.brand.name,
  },
  offers: {
    '@type': 'Offer',
    url: fullUrl,
    priceCurrency: globalContent.getCurrency,
    price: pricePerSquareMeter.value
      ? Math.round(pricePerSquareMeter.value * 100) / 100
      : parseFloat(productItem.priceInfo.price),
    ...(pricePerSquareMeter.value && {
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: Math.round(pricePerSquareMeter.value * 100) / 100,
        priceCurrency: globalContent.getCurrency,
        unitCode: conversionType.value === 'm' ? 'MTR':'MTK',
        unitText: conversionType.value === 'm' ? 'meter':'square meter',
      },
    }),
    itemCondition: 'https://schema.org/NewCondition',
    availability: activeVariant?.value?.canAddToCart ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
  },
} as any;

if (productItem.priceInfo.priceValidUntil) {
  jsonLd.offers.priceValidUntil = productItem.priceInfo.priceValidUntil;
}

// ProductSurfaceColour is a variant parametric, not a product parametric
const productSurfaceColour = activeVariant.value.parametricByCode('ProductSurfaceColour');

if (productSurfaceColour?.value) {
  jsonLd.offers.color = productSurfaceColour.value;
}

useHead(head);
useJsonld(jsonLd);
useSeoMeta(seoMeta);

// datalayer
let hostname = '';
if (import.meta.server) {
  hostname = nuxtApp?.ssrContext?.event?.node?.req?.headers?.host ?? '';
} else {
  hostname = window.location.host;
}

const foundDomain = domainMapper.find((d: { domain: any; }) => d.domain === hostname);
const activeVariantUrl = activeVariant.value?.url || '';

// Always set canonical to b2c version
let canonicalPath = route.path.replace(activeVariantUrl, productItem.url);
if (pageType === 'b2b' && globalContent.domainMap?.b2b?.productBasePath) {
  canonicalPath = canonicalPath.replace(`/${globalContent.domainMap.b2b.productBasePath}/`, `/${globalContent.domainMap?.b2c?.productBasePath || 'products'}/`);
}

const canonicalUrl = 'https://' + foundDomain?.domain + canonicalPath;
useHead(() => ({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl,
    },
  ],
}));

const { trackProductView } = useAlgoliaInsights();

onMounted(async()=> {
  sendDataLayer(
    'view_item',
    [ createDataLayerItem(productItem) ]
  );

  if (productData.value?.hits?.[0]?.objectID) {
    trackProductView(
      productData.value.hits[0].objectID,
      algoliaIndex
    );
  }
});

</script>
